import { Controller } from '@hotwired/stimulus';
import { FetchRequest } from '@rails/request.js';
import {
  elementVisible,
  eventListener,
  formatBytes,
} from '@inked/utilities';
import Mustache from 'mustache';

export class FileUploadController extends Controller {
  static targets = [
    'file',
    'files',
    'fileTemplate',
    'input',
    'selected',
    'selectedText',
    'selectedTextTemplate',
  ];
  static values = {
    failedLabel: String,
    src: String,
    uploadingLabel: String,
  };

  add({ detail: { files } }) {
    const data = new DataTransfer();

    for (const file of [...files].slice(0, this.inputTarget.multiple ? undefined : 1)) {
      data.items.add(file);
    }

    this.inputTarget.files = data.files;
    this.inputTarget.dispatchEvent(new Event('input'));
  }

  connect() {
    this.stopInputEvent = eventListener(this.inputTarget, 'input', event => this.#onInput(event));

    if (this.inputTarget.value) this.inputTarget.dispatchEvent(new Event('input'));
  }

  disconnect() {
    this.stopInputEvent();
  }

  dragover(event) {
    event.preventDefault();
  }

  drop(event) {
    if (!event.dataTransfer?.files?.length) return;

    event.preventDefault();

    if (this.inputTarget.disabled) return;

    this.add({ detail: { files: event.dataTransfer.files } });
  }

  remove(event) {
    this.#removeFile(event.target.closest('[data-file-upload-target="file"]'));
    this.#setSelected();

    this.inputTarget.dispatchEvent(new Event('change'));
  }

  #clearFiles() {
    if (this.inputTarget.multiple) {
      if (!this.srcValue) this.filesTarget.innerHTML = '';

      return;
    }

    this.fileTargets.forEach(file => {
      const fileRemove = file.querySelector('[name*="[remove_"]');

      if (fileRemove) {
        fileRemove.disabled = true;
        file.classList.add('hidden');
      } else {
        file.remove();
      }
    });
  }

  #fileData(file, index = 0) {
    const size = formatBytes(file.size);

    return {
      description: this.srcValue ? this.uploadingLabelValue : size,
      index,
      name: file.name,
      position: index + 1,
      size,
      status: this.srcValue ? 'uploading' : '',
      thumbnail: URL.createObjectURL(file),
    };
  }

  #fileHTML(data) {
    return Mustache.render(this.fileTemplateTarget.innerHTML, data);
  }

  #onInput(event) {
    const files = [...event.target.files];

    if (files.length === 0) return;

    const container = this.hasFilesTarget ? this.filesTarget : this.selectedTarget;

    this.#clearFiles();

    files.forEach(file => {
      const data = this.#fileData(file, this.inputTarget.multiple ? this.fileTargets.length : 0);

      container.insertAdjacentHTML('beforeend', this.#fileHTML(data));

      if (this.srcValue) this.#uploadFile(container.lastElementChild, file, data);
    });

    this.#setSelected();
  }

  #removeFile(fileContainer) {
    const fileRemove = fileContainer.querySelector('[name*="[_destroy]"], [name*="[remove_"]');

    if (fileRemove) {
      fileRemove.value = 1;
      fileContainer.classList.add('hidden');

      return;
    }

    if (this.inputTarget.multiple) {
      const data = new DataTransfer();
      const fileIndex = [...this.filesTarget.children].indexOf(fileContainer);

      [...this.inputTarget.files]
        .filter((_, index) => index !== fileIndex)
        .forEach(file => data.items.add(file));

      this.inputTarget.files = data.files;

      fileContainer.remove();

      return;
    }

    this.inputTarget.value = null;

    fileContainer.remove();
    this.#restoreFiles();
  }

  #restoreFiles() {
    this.fileTargets.forEach(file => {
      const fileRemove = file.querySelector('[name*="[remove_"]');

      fileRemove.disabled = false;
      file.classList.toggle('hidden', fileRemove.value === '1');
    });
  }

  #setSelected() {
    if (this.hasSelectedTextTarget) this.selectedTextTarget.remove();
    if (!this.hasFilesTarget && this.#fileCount > 0) return;

    this.selectedTarget.insertAdjacentHTML(
      'beforeend',
      Mustache.render(
        this.selectedTextTemplateTarget.innerHTML,
        {
          count: this.#fileCount,
          label: this.#fileLabel,
        }
      ),
    );
  }

  async #upload(file) {
    const data = new FormData();

    data.append('files[]', file);

    const request = new FetchRequest(
      'post',
      this.srcValue,
      {
        body: data,
      },
    );
    const response = await request.perform().catch(() => null);

    if (!response?.ok) return null;

    const json = await response.json;

    return json.files[0];
  }

  async #uploadFile(element, file, data) {
    const upload = await this.#upload(file);

    if (!element.isConnected) return;

    if (!upload || upload.errors) {
      const errors = upload?.errors || [this.failedLabelValue];

      this.dispatch('error', { detail: { errors } });

      element.outerHTML = this.#fileHTML({
        ...data,
        description: errors.join(', '),
        status: 'error',
      });

      return;
    }

    element.outerHTML = this.#fileHTML({
      ...data,
      description: data.size,
      status: '',
      uploadFileId: upload.id,
    });
  }

  get #fileCount() {
    return this.fileTargets.filter(file => elementVisible(file)).length;
  }

  get #fileLabel() {
    return this.#fileCount > 1 ? 'files' : 'file';
  }
}
